import React from 'react';
import { Mail } from 'lucide-react';
import { RequestReviewToggle } from './RequestReviewToggle';
import EmailChipInput, { EmailChip } from '@/components/reviews/request/EmailChipInput';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useUploadContext } from './contexts/UploadContext';
import { useAuth } from '@/contexts/AuthContext';

const MAX_INVITES_PER_PAPER = 3;
const NOTE_MAX = 500;

export const ReviewRequestSection: React.FC = () => {
  const {
    requestReview,
    setRequestReview,
    inviteChips,
    setInviteChips,
    invitePersonalNote,
    setInvitePersonalNote,
    inviteWarranty,
    setInviteWarranty,
  } = useUploadContext();
  const { user } = useAuth();

  return (
    <div className="pt-4 border-t border-border">
      <RequestReviewToggle
        requestReview={requestReview}
        setRequestReview={setRequestReview}
      />

      {requestReview && (
        <div className="pl-6 mt-4 space-y-4 animate-fade-in">
          <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50 border border-border/60 text-xs text-muted-foreground">
            <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <p>
              Invite up to {MAX_INVITES_PER_PAPER} reviewers by email. They'll receive a one-time
              link with your personal note. Invites expire after 14 days.
            </p>
          </div>

          <EmailChipInput
            value={inviteChips}
            onChange={setInviteChips}
            maxChips={MAX_INVITES_PER_PAPER}
            ownEmail={user?.email ?? undefined}
            label="Reviewer emails"
            id="upload-invite-emails"
          />

          <div className="space-y-2">
            <Label htmlFor="upload-invite-note">
              Personal note <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="upload-invite-note"
              value={invitePersonalNote}
              onChange={(e) => setInvitePersonalNote(e.target.value.slice(0, NOTE_MAX))}
              maxLength={NOTE_MAX}
              rows={3}
              placeholder="Briefly explain why you're inviting them and what feedback you're hoping for."
            />
            <p className="text-xs text-muted-foreground text-right">
              {invitePersonalNote.length}/{NOTE_MAX}
            </p>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="upload-invite-warranty"
              checked={inviteWarranty}
              onCheckedChange={(checked) => setInviteWarranty(checked === true)}
            />
            <Label
              htmlFor="upload-invite-warranty"
              className="text-xs text-muted-foreground leading-snug cursor-pointer"
            >
              I confirm these are professional acquaintances or colleagues whose academic
              expertise is relevant to this paper, and I have a legitimate basis to contact them.
            </Label>
          </div>
        </div>
      )}
    </div>
  );
};
